import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import { NoteIcon, PackageIcon } from '@sharedCR/coreDesignSystem/icons';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useSort from 'hooks/useSort';
import { logger } from 'utils/logger';

import { openDialogAction, showMessageAction } from 'store/global/actions';
import { correctGeneratedOutsideFormDeliveryDatesAction } from 'store/loanApplications/actions';

import { signerTableWrapperStyles, tableCellStyles } from '../../DisclosureDelivery.styles';
import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';
import useDisclosureDelivery from '../../hooks/useDisclosureDelivery';
import useSignatureRequestDocumentViewer from '../../hooks/useSignatureRequestDocumentViewer';
import EditGeneratedOutsideDatesDialog from '../EditGeneratedOutsideDatesDialog';

import { getSignerTableConfig } from './DisclosureSignerTable.config';

const defaultOrder = { direction: 'desc', id: 'sentDate' };
const defaultSignatureItems = [];

const DisclosureSignerTable = ({
  isLoading,
  setIsLoading,
  applicationId,
  formBundleSignatureRequestId,
  formBundleName,
  isGeneratedOutside = false,
  isSignable = true,
  signatureItems = defaultSignatureItems,
  createdDate,
  generatedDate,
  onResendPackage,
}) => {
  const dispatch = useDispatch();

  const { loanId, isReadAllowed, hostConfig, refresh } = useDisclosureDelivery();

  const { order, onRequestSort } = useSort(defaultOrder);

  const isDownloadable = !!formBundleName && !!formBundleSignatureRequestId;

  const orderedSignatureItems = useMemo(
    () =>
      _.orderBy(
        signatureItems.map(item => ({ ...item, generatedDate })),
        [item => item[order.id]?.toString()?.toLowerCase()],
        [order.direction],
      ),
    [signatureItems, generatedDate, order],
  );

  const { onViewDocuments } = useSignatureRequestDocumentViewer({
    loanId,
    signatureId: formBundleSignatureRequestId,
    fileName: formBundleName,
    fileDate: createdDate,
    isDownloadable,
    setIsLoading,
  });

  const editableRecipients = useMemo(
    () =>
      signatureItems.map(
        ({ formBundleSignatureRequestItemId, signerFullName, sentDate, signedDate }) => ({
          recipientRequestId: formBundleSignatureRequestItemId,
          signerFullName,
          sentDate,
          eSignedDate: signedDate,
        }),
      ),
    [signatureItems],
  );

  const onSubmitEditDates = useCallback(
    async recipients => {
      try {
        await dispatch(
          correctGeneratedOutsideFormDeliveryDatesAction({
            formDeliveryRequestId: formBundleSignatureRequestId,
            applicationId,
            recipients,
            silent: true,
          }),
        );

        dispatch(showMessageAction({ message: 'Delivery dates updated', variant: 'success' }));
        await refresh();

        return true;
      } catch (error) {
        dispatch(
          showMessageAction({ message: 'Could not update the delivery dates', variant: 'error' }),
        );
        logger.error(error);

        return false;
      }
    },
    [dispatch, formBundleSignatureRequestId, applicationId, refresh],
  );

  const onEditDates = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Edit dates',
        body: () => (
          <EditGeneratedOutsideDatesDialog
            recipients={editableRecipients}
            onSubmit={onSubmitEditDates}
          />
        ),
      }),
    );
  }, [dispatch, editableRecipients, onSubmitEditDates]);

  const tableConfig = useMemo(
    () =>
      getSignerTableConfig({
        enableRowActions: hostConfig.enableRowActions,
        formBundleName,
        formDeliveryRequestId: formBundleSignatureRequestId,
        isGeneratedOutside,
        isSignable,
        onEditDates,
      }),
    [
      hostConfig.enableRowActions,
      formBundleName,
      formBundleSignatureRequestId,
      isGeneratedOutside,
      isSignable,
      onEditDates,
    ],
  );

  return (
    <Box sx={signerTableWrapperStyles}>
      <Box className="flex gap-8">
        {hostConfig.enablePackageResend && !!onResendPackage && (
          <Button
            data-testid={DisclosureDeliveryTestIds.RESEND_PACKAGE_BUTTON}
            disabled={isLoading}
            startIcon={<PackageIcon />}
            onClick={onResendPackage}
          >
            Resend package
          </Button>
        )}
        <Button
          data-testid={DisclosureDeliveryTestIds.VIEW_DOCUMENTS_BUTTON}
          disabled={isLoading || !isReadAllowed}
          startIcon={<NoteIcon />}
          onClick={onViewDocuments}
        >
          View Documents
        </Button>
      </Box>

      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        customStyles={tableCellStyles}
        data={orderedSignatureItems}
        data-testid={DisclosureDeliveryTestIds.getSignerTableId(formBundleSignatureRequestId)}
        isCustomDataDefinition={!orderedSignatureItems.length}
        order={order}
        onRequestSort={onRequestSort}
      />
    </Box>
  );
};

DisclosureSignerTable.propTypes = {
  ...WithLoadingPropTypes,
  applicationId: PropTypes.string,
  formBundleSignatureRequestId: PropTypes.string,
  formBundleName: PropTypes.string,
  isGeneratedOutside: PropTypes.bool,
  isSignable: PropTypes.bool,
  signatureItems: PropTypes.array,
  createdDate: PropTypes.string,
  generatedDate: PropTypes.string,
  onResendPackage: PropTypes.func,
};

export default withLoading(DisclosureSignerTable);
